import { snakeCase } from 'lodash-es';
import { create } from 'zustand';

import { AUTH_CONST } from '@/constant';
import { storage } from '@/package';
import {
  TenantStorage,
  type VStorageConfig,
  type VStorageProgress,
} from '@/repository';
import type {
  TenantStorageStoreActions,
  TenantStorageStoreState,
} from '@/type';

const KEYS = AUTH_CONST.KEYS;
const INIT_STATE = {
  config: {
    endpoint: '',
    idToken: '',
    tenant: {
      id: '',
      tier: '',
    },
  },
  connected: false,
  storageAccessibleBucketList: storage.local.get<string[]>(
    KEYS.STORAGE_STORAGE_ACCESSIBLE_BUCKET_LIST,
  ),
};

const setLocalStorage = <T extends object>(keyVal: T) => {
  Object.entries(keyVal).forEach(([key, val]) => {
    storage.local.set<T>(`STORAGE_${snakeCase(key).toUpperCase()}`, val);
  });
};

const tenantStorage = new TenantStorage({});

const useTenantStorageStore = create<
  TenantStorageStoreState & TenantStorageStoreActions
>((set, get) => {
  const assert = () => {
    const { storageCurrentBucket } = get();

    let errorMessage = '';

    if (!tenantStorage.config?.endpoint) errorMessage = 'Undefined s3 endpoint';
    if (!storageCurrentBucket) errorMessage = 'You must need select bucket';

    if (errorMessage) throw new Error(errorMessage);
  };

  const changeConfig = (config: Partial<VStorageConfig>) => {
    connectStorage({
      ...tenantStorage.config,
      ...config,
    });
  };

  const isConnected = () => get().connected;

  const setStorageAccessibleBucketList = (
    storageAccessibleBucketList: string[],
  ) => {
    setLocalStorage({ storageAccessibleBucketList });
    set({ storageAccessibleBucketList });
  };
  const setStorageCurrentBucket = (storageCurrentBucket: string) => {
    set({ storageCurrentBucket });
    tenantStorage.selectBucket(storageCurrentBucket);
  };

  const connectStorage = (config: VStorageConfig) => {
    tenantStorage.changeConfig(config);

    const currentBucket = get().storageCurrentBucket;
    const bucket = currentBucket
      ? currentBucket
      : get().storageAccessibleBucketList?.[0];

    set({ config: tenantStorage.config });

    if (bucket) {
      set({ connected: true });

      return Promise.resolve(bucket).then(setStorageCurrentBucket);
    }

    return Promise.resolve();
  };

  const createDirectory = async ({ key }: { key: string }) => {
    assert();

    return tenantStorage.createDirectory({ key });
  };

  const downloadDirectory = (param: {
    key: string;
    downloadCallback?: (progress: VStorageProgress) => void;
  }) => {
    assert();

    return tenantStorage.downloadDirectory({
      ...param,
    });
  };
  const downloadFile = (param: {
    key: string;
    downloadCallback?: (progress: VStorageProgress) => void;
  }) => {
    assert();

    return tenantStorage.downloadFile({
      ...param,
    });
  };

  const readDirectory = ({
    prefix,
    recursive,
    startAfter,
  }: {
    prefix: string;
    recursive?: boolean;
    startAfter?: string;
  }) => {
    assert();

    return tenantStorage.readDirectory({
      prefix,
      recursive,
      startAfter,
    });
  };
  const readDirectoryRecursive = ({
    prefix,
    progressCallback,
  }: {
    prefix: string;
    progressCallback?: (progress: VStorageProgress) => void;
  }) => {
    assert();

    return tenantStorage.readDirectoryRecursive({
      prefix,
      progressCallback,
    });
  };
  const readFile = (param: { key: string; timestamp?: boolean }) => {
    assert();

    return tenantStorage.readFile({
      ...param,
    });
  };
  const readFileList = (param: {
    keyList: string[];
    maxQueueSize?: number;
  }) => {
    assert();

    return tenantStorage.readFileList({
      ...param,
    });
  };

  const removeDirectory = (param: { key: string }) => {
    assert();

    return tenantStorage.removeDirectory({
      ...param,
    });
  };
  const removeFile = (param: { key: string }) => {
    assert();

    return tenantStorage.removeFile({
      ...param,
    });
  };

  const uploadFile = (param: {
    presignURL: string;
    file: Blob;
    progressCallback?: (progress: VStorageProgress) => void;
  }) => {
    assert();

    return tenantStorage.uploadFile({
      presignURL: param.presignURL,
      body: param.file,
      progressCallback: param.progressCallback,
    });
  };

  return {
    ...INIT_STATE,
    storageCurrentBucket: INIT_STATE.storageAccessibleBucketList?.[0],

    changeConfig,
    connectStorage,
    createDirectory,

    downloadDirectory,
    downloadFile,

    isConnected,

    readDirectory,
    readDirectoryRecursive,
    readFile,
    readFileList,
    removeFile,
    removeDirectory,

    uploadFile,

    setStorageAccessibleBucketList,
    setStorageCurrentBucket,
  };
});

export default useTenantStorageStore;
